'use client';
import React, { useEffect, useState } from 'react';
import { Phone, FileText, Clock, Smile } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

export default function HowItWorksSection() {
  const [flRegionCount, setFlRegionCount] = useState(10);

  useEffect(() => {
    const supabase = createClient();
    const fetchCount = async () => {
      const { data } = await supabase?.from('regions')?.select('id, market')?.eq('is_active', true)?.eq('market', 'FL');
      if (data?.length) setFlRegionCount(data?.length);
    };
    void fetchCount();
  }, []);

  const steps = [
    {
      number: '1',
      icon: Phone,
      title: 'Choose Your Regions',
      description: `Select from ${flRegionCount} Florida regions or Puerto Rico coverage areas. Each region reaches 1,000 targeted households.`,
    },
    {
      number: '2',
      icon: FileText,
      title: 'Pick Your Placement',
      description: 'Choose from Standard Inside Page, Inside/Back Covers, or premium Front/Back Cover positions.',
    },
    {
      number: '3',
      icon: Clock,
      title: 'Upload Artwork & Pay',
      description: "Submit your 8.5×5.5 ad creative or request our design service. Pay online — one-time or 3-issue monthly.",
    },
    {
      number: '4',
      icon: Smile,
      title: 'Get in Front of Customers',
      description: 'Your ad prints and ships every 5 weeks. Track your order status from your advertiser dashboard.',
    },
  ];

  return (
    <section id="how-it-works" className="py-20">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
        {/* Header */}
        <div className="mb-12">
          <p className="text-xs font-semibold text-brand-400 uppercase tracking-widest mb-3">Simple Process</p>
          <h2 className="font-display text-4xl sm:text-5xl font-800 leading-tight">
            <span className="text-white">How It </span>
            <span className="text-gradient">Works</span>
          </h2>
        </div>

        {/* Steps grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {steps?.map((step, i) => (
            <div
              key={`step-${step?.number}`}
              className="glass-card glass-card-hover rounded-2xl p-6 relative group"
            >
              {/* Connector line (desktop) */}
              {i < steps?.length - 1 && (
                <div className="hidden xl:block absolute top-8 -right-2 w-4 h-px bg-brand-700/50 z-10" />
              )}

              <div className="flex items-start gap-4">
                {/* Step badge */}
                <div className="step-badge shrink-0 group-hover:shadow-glow transition-all duration-200">
                  {step?.number}
                </div>

                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <step.icon size={16} className="text-brand-400" />
                    <h3 className="font-display font-700 text-base text-white">{step?.title}</h3>
                  </div>
                  <p className="text-sm text-slate-400 leading-relaxed">{step?.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}